import type { IntelWorkspaceItem } from '@pn/core/domain/intel/intel-workspace-item';
import type { Filter, LinkOperator, Sort } from '@pn/core/domain/query';
import { getMapping } from '@pn/core/storage/workspace/mappings';
import { apiDataItemMapper } from '@pn/services/api/data/apiDataItemMapper';
import { pnApiClient } from '@pn/services/api/pnApiClient';
import { isNil } from 'lodash-es';
import type { ApiIntelItem } from './types';

type ApiQuery = {
  fields: string[];
  requested_ids: string[];
  filters: Filter[];
  filters_link_operator: LinkOperator;
  sorts: Sort[];
  limit: number | undefined;
};

// copied from intel/src/ui/mapping
function getIntelMapping(intelMappingItem: IntelWorkspaceItem) {
  return getMapping(intelMappingItem.dataType);
}

// TODO: Undo the sins
const requestData = async ({
  intelWorkspaceItem,
  geometry,
  province,
}: {
  intelWorkspaceItem: IntelWorkspaceItem;
  geometry?: GeoJSON.Geometry;
  province?: string;
}) => {
  const {
    query: { dataType, filters, filtersLinkOperator, sorts, limit },
  } = intelWorkspaceItem;
  // TODO: ugly hack for the joined table of this endpoint

  const mapping = getIntelMapping(intelWorkspaceItem);
  if (isNil(mapping)) {
    throw new Error('Mapping not found for intel workspace item');
  }

  let modifiedFilters = [...filters];
  if (!isNil(province) && isNil(intelWorkspaceItem.linkedReportId)) {
    if (
      dataType === 'well_licenses' ||
      dataType.includes('ip3') ||
      dataType.includes('ip12') ||
      dataType.includes('wells_by')
    ) {
      modifiedFilters.push({
        field: 'wellbores.province',
        operator: '=',
        value: province,
      });
    } else if (dataType === 'well_license_transfers') {
      modifiedFilters.push({
        field: 'license_transfers_by_company.province',
        operator: '=',
        value: province,
      });
    } else if (dataType === 'land_results' || dataType === 'land_postings') {
      modifiedFilters.push({
        field: 'market_intel_land_postings.province',
        operator: '=',
        value: province,
      });
    } else {
      modifiedFilters.push({
        field: 'province',
        operator: '=',
        value: province,
      });
    }
  }

  if (dataType === 'aer_transfers') {
    const provinceFilter = modifiedFilters.find(
      ({ field }) => field === 'province'
    );
    if (!isNil(provinceFilter)) {
      if (provinceFilter?.value !== 'AB') {
        return [];
      }
      modifiedFilters = modifiedFilters.filter(
        ({ field }) => field !== 'province'
      );
    }
  }

  if (!isNil(geometry) && isNil(intelWorkspaceItem.linkedReportId)) {
    const wellboresDataTypes = [
      'well_licenses',
      'liquid_ip3',
      'liquid_ip12',
      'gas_ip3',
      'gas_ip12',
      'wells_by_production',
      'wells_by_gas_production',
      'wells_by_liquid_production',
      'wellbore_recompletes',
    ];
    if (wellboresDataTypes.includes(dataType)) {
      modifiedFilters.push({
        field: 'wellbores.geom',
        operator: 'intersects',
        value: geometry,
      });
    } else if (dataType === 'expired_minerals' || dataType === 'zone_changes') {
      modifiedFilters.push({
        field: 'mineral_rights_geometries.land_geom',
        operator: 'intersects',
        value: geometry,
      });
    } else {
      modifiedFilters.push({
        field: 'geom',
        operator: 'intersects',
        value: geometry,
      });
    }
  }

  const payload: ApiQuery = {
    fields: [],
    requested_ids: [],
    filters: modifiedFilters,
    filters_link_operator: filtersLinkOperator,
    sorts,
    limit: intelWorkspaceItem.query.limit,
  };

  let response: any[];
  // TODO: consolidate this into a single endpoint and undo this switch statement
  switch (dataType) {
    case 'liquid_ip3':
    case 'liquid_ip12':
    case 'gas_ip3':
    case 'gas_ip12':
      response = await pnApiClient.request<ApiIntelItem[]>({
        url: `v2/wells/top_wells_by_ip/${dataType}`,
        method: 'POST',
        payload,
      });
      break;
    case 'wells_by_production':
      response = await pnApiClient.request<ApiIntelItem[]>({
        url: `v2/wells/top_producing/boe?limit=${limit}`,
        method: 'POST',
        payload,
      });
      break;
    case 'wells_by_gas_production':
      response = await pnApiClient.request<ApiIntelItem[]>({
        url: `v2/wells/top_producing/gas?limit=${limit}`,
        method: 'POST',
        payload,
      });
      break;
    case 'wells_by_liquid_production':
      response = await pnApiClient.request<ApiIntelItem[]>({
        url: `v2/wells/top_producing/liquid?limit=${limit}`,
        method: 'POST',
        payload,
      });
      break;
    default:
      // this is the endpoint we want to consolidate to eventually
      response = await pnApiClient.request<any[]>({
        url: `v2/data/${dataType}`,
        method: 'POST',
        payload,
      });
  }

  if (dataType == 'zone_changes') {
    response = response.map((item, index) => ({
      ...item,
      internal_id: index,
      _id: index,
    }));
  }

  return response.map(apiDataItemMapper(mapping).toDomainDataItem);
};

export const apiIntelDynamicDataProvider = {
  getData: requestData,
};
